import { startRegistration } from '@simplewebauthn/browser';
import { useCallback, useEffect, useState } from 'react';
import { ConfirmModal } from '../components/ConfirmModal';
import { type AdminPasskey, apiClient } from '../lib/api-client';
import { describePasskeyError, isPasskeyCancelled } from '../lib/passkey-errors';
import { formatDateTime } from '../utils/datetime';

const DEFAULT_PASSKEY_NAME = 'Passkey';
const PASSKEY_NAME_MAX_LENGTH = 100;

const INPUT_CLASS =
  'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-400';

const SECONDARY_BUTTON_CLASS =
  'rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600';

const DATE_TIME_OPTIONS = { dateStyle: 'medium', timeStyle: 'short' } as const;

function formatPasskeyDate(value: string | null): string {
  return formatDateTime(value, DATE_TIME_OPTIONS) || 'Never';
}

function describeDeviceType(passkey: AdminPasskey): string {
  const type = passkey.deviceType === 'multiDevice' ? 'Synced passkey' : 'Device-bound passkey';
  return passkey.backedUp ? `${type} (backed up)` : type;
}

export default function PasskeysPage() {
  const [passkeys, setPasskeys] = useState<AdminPasskey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [renaming, setRenaming] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<AdminPasskey | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadPasskeys = useCallback(async () => {
    try {
      const result = await apiClient.listPasskeys();
      setPasskeys(result.passkeys);
    } catch (err) {
      setError(describePasskeyError(err, 'Failed to load passkeys'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPasskeys();
  }, [loadPasskeys]);

  const handleAdd = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setAdding(true);

    try {
      const { challengeId, options } = await apiClient.getPasskeyRegistrationOptions();
      const response = await startRegistration({ optionsJSON: options });
      await apiClient.verifyPasskeyRegistration({
        challengeId,
        response,
        name: newName.trim() || DEFAULT_PASSKEY_NAME
      });
      setNewName('');
      setSuccess('Passkey added');
      await loadPasskeys();
    } catch (err) {
      if (isPasskeyCancelled(err)) {
        setSuccess('');
        setError('Passkey registration was cancelled.');
      } else {
        setError(describePasskeyError(err, 'Failed to add passkey'));
      }
    } finally {
      setAdding(false);
    }
  };

  const startRename = (passkey: AdminPasskey) => {
    setEditingId(passkey.id);
    setEditingName(passkey.name);
    setError('');
    setSuccess('');
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleRename = async (e: React.FormEvent<HTMLFormElement>, id: string) => {
    e.preventDefault();
    const name = editingName.trim();
    if (!name) {
      setError('Passkey name cannot be empty');
      return;
    }

    setRenaming(true);
    setError('');
    try {
      await apiClient.renamePasskey(id, name);
      setSuccess('Passkey renamed');
      cancelRename();
      await loadPasskeys();
    } catch (err) {
      setError(describePasskeyError(err, 'Failed to rename passkey'));
    } finally {
      setRenaming(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    setDeleting(true);
    setError('');
    setSuccess('');
    try {
      await apiClient.deletePasskey(deleteTarget.id);
      setSuccess('Passkey deleted');
      await loadPasskeys();
    } catch (err) {
      setError(describePasskeyError(err, 'Failed to delete passkey'));
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const isLastPasskey = passkeys.length <= 1;

  return (
    <div className='mx-auto max-w-3xl'>
      <div className='mb-6'>
        <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>Passkeys</h1>
        <p className='mt-2 text-sm text-gray-600 dark:text-gray-400'>
          Passkeys are the only way to sign in to the admin panel. Register more than one (for
          example a laptop and a security key) so you are not locked out. The last remaining passkey
          cannot be deleted.
        </p>
      </div>

      {error && (
        <div className='mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900 dark:text-red-200'>
          {error}
        </div>
      )}

      {success && (
        <div className='mb-4 rounded-md bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900 dark:text-green-200'>
          {success}
        </div>
      )}

      <div className='mb-6 rounded-lg bg-white p-6 shadow dark:bg-gray-800'>
        <h2 className='mb-4 text-lg font-semibold text-gray-900 dark:text-white'>Add a passkey</h2>
        <form onSubmit={e => void handleAdd(e)} className='flex flex-col gap-3 md:flex-row'>
          <input
            type='text'
            value={newName}
            onChange={e => setNewName(e.target.value)}
            maxLength={PASSKEY_NAME_MAX_LENGTH}
            className={INPUT_CLASS}
            placeholder='Passkey name (e.g. MacBook, YubiKey)'
            aria-label='New passkey name'
          />
          <button
            type='submit'
            disabled={adding}
            className='rounded-md bg-blue-600 px-4 py-2 whitespace-nowrap text-white transition-colors hover:bg-blue-700 disabled:bg-gray-400 dark:bg-blue-500 dark:hover:bg-blue-600 dark:disabled:bg-gray-600'
          >
            {adding ? 'Waiting for passkey...' : 'Add passkey'}
          </button>
        </form>
      </div>

      <div className='rounded-lg bg-white shadow dark:bg-gray-800'>
        {loading ? (
          <div className='p-6 text-gray-600 dark:text-gray-400'>Loading...</div>
        ) : passkeys.length === 0 ? (
          <div className='p-6 text-gray-600 dark:text-gray-400'>No passkeys registered.</div>
        ) : (
          <ul className='divide-y divide-gray-200 dark:divide-gray-700'>
            {passkeys.map(passkey => (
              <li key={passkey.id} className='flex flex-col gap-3 p-4 md:flex-row md:items-center'>
                <div className='min-w-0 flex-1'>
                  {editingId === passkey.id ? (
                    <form
                      onSubmit={e => void handleRename(e, passkey.id)}
                      className='flex flex-col gap-2 md:flex-row'
                    >
                      <input
                        type='text'
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        maxLength={PASSKEY_NAME_MAX_LENGTH}
                        className={INPUT_CLASS}
                        aria-label='Passkey name'
                        autoFocus
                      />
                      <div className='flex gap-2'>
                        <button
                          type='submit'
                          disabled={renaming}
                          className='rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-blue-700 disabled:bg-gray-400 dark:bg-blue-500 dark:hover:bg-blue-600 dark:disabled:bg-gray-600'
                        >
                          Save
                        </button>
                        <button
                          type='button'
                          onClick={cancelRename}
                          disabled={renaming}
                          className={SECONDARY_BUTTON_CLASS}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <p className='truncate font-medium text-gray-900 dark:text-white'>
                      {passkey.name}
                    </p>
                  )}
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                    {describeDeviceType(passkey)} · Created {formatPasskeyDate(passkey.createdAt)} ·
                    Last used {formatPasskeyDate(passkey.lastUsedAt)}
                  </p>
                </div>

                {editingId !== passkey.id && (
                  <div className='flex gap-2'>
                    <button
                      type='button'
                      onClick={() => startRename(passkey)}
                      className={SECONDARY_BUTTON_CLASS}
                    >
                      Rename
                    </button>
                    <button
                      type='button'
                      onClick={() => setDeleteTarget(passkey)}
                      disabled={isLastPasskey}
                      title={
                        isLastPasskey ? 'The last remaining passkey cannot be deleted' : undefined
                      }
                      className='rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-700 dark:bg-gray-700 dark:text-red-300 dark:hover:bg-red-900'
                    >
                      Delete
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title='Delete passkey'
        message={`Delete "${deleteTarget?.name ?? ''}"? You will no longer be able to sign in with it.`}
        confirmText='Delete'
        variant='danger'
        isLoading={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
